import { useState, useEffect } from "react";
import {
  Box,
  Button,
  PasswordInput,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UseAuth } from "/@/contexts";

import {
  STYLE,
  checkPasswordStrength,
  isUserStaffNotAdmin,
} from "@jasper/shared";
import { AxiosError } from "axios";
import { notifications } from "@mantine/notifications";

const Login = () => {
  const [searchParams] = useSearchParams();
  const { login, me } = UseAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<{
    email: string;
    password: string;
    newPassword?: string;
    newPasswordConfirmation?: string;
  }>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const redirectAfterLogin = usr => {
    if (isUserStaffNotAdmin(usr)) {
      navigate("/groups");
      return;
    }
    const next = searchParams.get("next") || `/home`;
    navigate(next);
  };

  const submit = async (value: {
    email: string;
    password: string;
    newPassword?: string;
  }) => {
    try {
      const usr = await login({
        email: value.email.trim(),
        password: value.password,
        newPassword: value.newPassword,
      });
      if (usr) {
        redirectAfterLogin(usr);
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response?.data.message === "MUST_CHANGE_PASSWORD") {
          setMustChangePassword(true);
        }

        if (e.response?.data.message === "WEAK_PASSWORD") {
          setError("newPassword", {
            type: "manual",
            message:
              "Password does not meet strength requirements (must contain at least 8 characters, including uppercase, lowercase, number and special character)",
          });
          return;
        }

        if (e.response?.data.message === "SAME_PASSWORD") {
          setError("newPassword", {
            type: "manual",
            message: "New password must be different from the current one",
          });

          return;
        }

        return;
      }

      notifications.show({
        title: "An error occurred. Please try again.",
        color: "red",
        message: "",
      });
    }
  };

  const isUserAlreadyConnected = async () => {
    const usr = await me();
    if (usr) {
      redirectAfterLogin(usr);
    }
    setLoading(false);
  };
  useEffect(() => {
    isUserAlreadyConnected();
  }, [false]);

  if (loading) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box component="form" onSubmit={handleSubmit(submit)}>
        <Title order={3} style={{ textAlign: "center" }}>
          {mustChangePassword
            ? "You must change your password"
            : "Welcome to Jasper Lab portal"}
        </Title>
        <Stack>
          <Stack gap="xs">
            <TextInput
              label="Email"
              placeholder="Your email address"
              {...register("email", { required: true })}
              error={
                errors.email?.type === "required" && "This field is mandatory"
              }
              data-testid="input-email"
            />
            <PasswordInput
              label="Password"
              placeholder={
                mustChangePassword ? "Your current password" : "Your password"
              }
              {...register("password", { required: true })}
              error={
                errors.password?.type === "required" &&
                "This field is mandatory"
              }
              data-testid="secure-input-password"
            />
          </Stack>
          {mustChangePassword && (
            <Stack gap="xs">
              <PasswordInput
                label="New Password"
                placeholder="Your new password"
                {...register("newPassword", {
                  required: true,
                  minLength: 8,
                  validate: value => value && checkPasswordStrength(value),
                })}
                error={
                  (errors.newPassword?.type === "required" &&
                    "This field is mandatory") ||
                  (errors.newPassword?.type === "minLength" &&
                    "Password must be at least 8 characters long") ||
                  errors.newPassword?.message
                }
                data-testid="secure-input-password"
              />
              <PasswordInput
                label="New Password confirmation"
                placeholder="Retype your new password"
                {...register("newPasswordConfirmation", {
                  required: true,
                  validate: (passwordConfirmation, formValues) => {
                    if (passwordConfirmation !== formValues.newPassword) {
                      return "Passwords do not match";
                    }
                    return true;
                  },
                })}
                error={
                  (errors.newPasswordConfirmation?.type === "required" &&
                    "This field is mandatory") ||
                  errors.newPasswordConfirmation?.message
                }
                data-testid="secure-input-password"
              />
            </Stack>
          )}
          <div style={{ marginTop: "1rem", textAlign: "center" }}>
            <Button
              type="submit"
              data-testid="submit-button-login"
              color={STYLE.primary}
            >
              Submit
            </Button>
          </div>
        </Stack>
      </Box>
    </div>
  );
};

export default Login;
